/**
 * iter657 — Smart Routing Indicator.
 *
 * Resolves where a menu item (by its category) will fire to before the
 * cashier hits "Pay". Mirrors the backend rules in
 * /api/cashier/categories  +  /api/cashier/printers:
 *
 *   • category.print_to is one of:
 *       - "none"                — no auto-print to prep
 *       - "kitchen"             — any kitchen printer in the branch
 *       - "bar"                 — any bar printer in the branch
 *       - "kitchen:<station>"   — specific kitchen station
 *       - "bar:<station>"       — specific bar station
 *
 *   • A printer matches when:
 *       - printer.type === role
 *       - branch_id matches OR is null (= "All branches")
 *       - is_active !== false
 *       - station === <station> when a station is requested
 *
 * Returned shape:
 *   {
 *     role:        'kitchen' | 'bar' | 'none' | 'unknown',
 *     station:     string | null,
 *     printerName: string | null,
 *     printerId:   string | null,
 *     label:       string   // "Kitchen", "Bar", "Kitchen — Cold Prep", or
 *                           //  "Receipt only" / "No printer configured"
 *     warning:     boolean  // true when print_to expects a printer but none
 *                           //  was found — the chip should turn amber.
 *   }
 */

export function parsePrintTo(printToRaw) {
  const v = String(printToRaw || '').trim().toLowerCase();
  if (!v || v === 'none') return { role: 'none', station: null };
  if (v === 'kitchen' || v === 'bar') return { role: v, station: null };
  if (v.startsWith('kitchen:')) return { role: 'kitchen', station: v.slice('kitchen:'.length) };
  if (v.startsWith('bar:')) return { role: 'bar', station: v.slice('bar:'.length) };
  return { role: 'unknown', station: null };
}

function findPrinter(printers, role, station, branchId) {
  const list = (printers || []).filter((p) => {
    if (p.is_active === false) return false;
    if ((p.type || '').toLowerCase() !== role) return false;
    if (branchId && p.branch_id && p.branch_id !== branchId) return false;
    return true;
  });
  if (station) {
    const s = station.trim().toLowerCase();
    const hit = list.find((p) => (p.station || '').trim().toLowerCase() === s);
    if (hit) return hit;
    return null; // station requested but not present → warning
  }
  // Prefer branch-specific over the "All branches" (null branch_id) printer
  // when both exist; we want the cashier to see *exactly* the device that fires.
  const specific = list.find((p) => p.branch_id && p.branch_id === branchId);
  return specific || list[0] || null;
}

export function resolveItemRoute({ categoryId, categories, printers, branchId }) {
  const cat = (categories || []).find((c) => c.id === categoryId);
  const printToRaw = cat ? cat.print_to : null;
  const { role, station } = parsePrintTo(printToRaw);

  if (role === 'none') {
    return {
      role: 'none', station: null, printerName: null, printerId: null,
      label: 'Receipt only', warning: false,
    };
  }
  if (role === 'unknown') {
    return {
      role: 'unknown', station: null, printerName: null, printerId: null,
      label: 'Unrouted', warning: true,
    };
  }
  const printer = findPrinter(printers, role, station, branchId);
  const prettyRole = role === 'kitchen' ? 'Kitchen' : 'Bar';
  let label = prettyRole;
  if (station) label = `${prettyRole} — ${station}`;
  if (!printer) {
    return {
      role, station, printerName: null, printerId: null,
      label: `${label} (no printer)`, warning: true,
    };
  }
  return {
    role,
    station,
    printerName: printer.name || null,
    printerId: printer.id || null,
    label: printer.name ? `${prettyRole}${station ? ` — ${station}` : ''} · ${printer.name}` : label,
    warning: false,
  };
}
